import {
  createRootRoute,
  Outlet,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useSession } from "@descope/react-sdk";
import { useEffect } from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { Toaster } from "@/components/ui/sonner";
import { configureAllSDKs } from "@/lib/harmony-sdk/config";
import { ConfigProvider, useConfig } from "@/lib/config/config-context";

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  return (
    <ConfigProvider>
      <RootContent />
    </ConfigProvider>
  );
}

function RootContent() {
  const { isAuthenticated, isSessionLoading } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { config: _config, needsAuth } = useConfig();

  useEffect(() => {
    // Configure SDKs when authenticated or in local mode
    if ((isAuthenticated && !isSessionLoading) || !needsAuth) {
      configureAllSDKs();
    }
  }, [isAuthenticated, isSessionLoading, needsAuth]);

  useEffect(() => {
    // Don't redirect if we're already on the login page or still loading
    if (location.pathname === "/login" || isSessionLoading) {
      return;
    }

    // Only redirect to login if we need auth and not authenticated
    if (needsAuth && !isAuthenticated) {
      void navigate({
        to: "/login",
        search: { redirect: location.pathname },
      });
    }
  }, [
    isAuthenticated,
    isSessionLoading,
    location.pathname,
    navigate,
    needsAuth,
  ]);

  // Don't show sidebar on login page
  const showSidebar = location.pathname !== "/login";

  return (
    <HotkeysProvider>
      {showSidebar ? (
        <SidebarProvider>
          <AppSidebar />
          <main className="relative flex max-h-dvh min-w-0 flex-1 flex-col overflow-hidden">
            <div className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto px-1 md:px-6 py-2 md:py-7">
              <Outlet />
            </div>
          </main>
        </SidebarProvider>
      ) : (
        <div className="bg-background min-h-screen">
          <Outlet />
        </div>
      )}
      <Toaster />
      <TanStackRouterDevtools />
    </HotkeysProvider>
  );
}
